import React from "react";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { DonutChart as TremorDonutChart } from "@tremor/react";
import { formatPercent } from "@/utils/numberFormat";
import { demographicsSnapshotLabel } from "./demographicsSnapshot";
import type {
  DemographicsChartItem,
  DemographicsCityItem,
} from "./instagram.types";

interface InstagramDemographicsProps {
  genderData: DemographicsChartItem[];
  ageData: DemographicsChartItem[];
  citiesData: DemographicsCityItem[];
  /**
   * O instante em que o Instagram foi consultado (`snapshot_at`).
   *
   * O Instagram só entrega a audiência de agora: não há gênero, idade ou cidade de uma data
   * passada. Por isso os três cards mostram a data do retrato, e não o período do filtro.
   */
  snapshotAt?: string | null;
  loading?: boolean;
}

/** Skeleton for the demographics section */
function DemographicsSkeleton(): React.ReactElement {
  return (
    <div className="grid gap-6 md:grid-cols-3">
      {[...Array(3)].map((_, i) => (
        <Card key={`demo-sk-${i}`}>
          <CardHeader>
            <Skeleton className="h-6 w-32" />
          </CardHeader>
          <CardContent>
            <Skeleton className="h-64 w-full" />
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

export default function InstagramDemographics({
  genderData,
  ageData,
  citiesData,
  snapshotAt = null,
  loading = false,
}: InstagramDemographicsProps): React.ReactElement {
  if (loading) return <DemographicsSkeleton />;

  const snapshotLabel = demographicsSnapshotLabel(snapshotAt);

  return (
    <div className="grid gap-6 md:grid-cols-3">
      {/* Gender */}
      <Card>
        <CardHeader>
          <CardTitle>Seguidores Por Gênero</CardTitle>
          <CardDescription>{snapshotLabel}</CardDescription>
        </CardHeader>
        <CardContent>
          <TremorDonutChart
            className="h-64"
            data={genderData}
            index="name"
            category="percentage"
            colors={["blue", "pink", "gray"]}
            valueFormatter={(value) => formatPercent(value)}
            showLabel={true}
            showAnimation={true}
          />
        </CardContent>
      </Card>

      {/* Age */}
      <Card>
        <CardHeader>
          <CardTitle>Seguidores Por Idade</CardTitle>
          <CardDescription>{snapshotLabel}</CardDescription>
        </CardHeader>
        <CardContent>
          <TremorDonutChart
            className="h-64"
            data={ageData}
            index="name"
            category="percentage"
            colors={[
              "amber",
              "red",
              "orange",
              "green",
              "blue",
              "violet",
              "cyan",
            ]}
            valueFormatter={(value) => formatPercent(value)}
            showLabel={true}
            showAnimation={true}
          />
        </CardContent>
      </Card>

      {/* Cities */}
      <Card>
        <CardHeader>
          <CardTitle>Seguidores Por Cidade</CardTitle>
          <CardDescription>{snapshotLabel}</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            {citiesData.slice(0, 8).map((city, index) => {
              const maxCount = citiesData[0]?.count ?? 1;
              const widthPct = Math.max(
                5,
                ((city.count ?? 0) / maxCount) * 100,
              );
              return (
                <div
                  key={city.city ?? index}
                  className="flex items-center gap-3"
                >
                  <span className="text-xs text-muted-foreground w-5 text-right flex-shrink-0">
                    {index + 1}.
                  </span>
                  <span className="text-xs flex-shrink-0 w-[140px] truncate">
                    {city.city ?? "N/D"}
                  </span>
                  <div className="flex-1 flex items-center gap-2">
                    <div className="flex-1 h-4 bg-muted rounded overflow-hidden">
                      <div
                        className="h-full bg-emerald-500 rounded"
                        style={{ width: `${widthPct}%` }}
                      />
                    </div>
                    <span className="text-xs font-medium w-[50px] text-right flex-shrink-0">
                      {(city.count ?? 0).toLocaleString()}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
